import * as React from "react";
import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import {
  DealerLocation,
  SetupType,
} from "../../../models/features/Integration/DmsSetup";
import { useSelector } from "react-redux";
import { AppState } from "../../../redux/reducer/rootReducer";
import { useDispatch } from "react-redux";
import {
  addLocation,
  addMappedLocations,
  removeLocation,
  useGetStoreLocationsMutation,
} from "../../../redux/reducer/features/Integration/DmsSetupSlice";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import AppConfirmDialog from "../../app/AppConfirmDialog";

interface DmsLocationsProps {
  locations: DealerLocation[];
  handleNext: any;
  handleBack: any;
}

const DmsLocations: React.FunctionComponent<DmsLocationsProps> = ({
  locations,
  handleBack,
  handleNext,
}) => {
  const dispatch = useDispatch();
  const { shopifyAccessToken, shopifyUrl, setupType } = useSelector(
    (state: AppState) => state.dmsReducer
  );
  const locationIds = useSelector(
    (state: AppState) => state.dmsReducer.locationIds
  );
  const [storeLocations, setStoreLocations] = React.useState<
    { id: number; name: string; address: string }[]
  >([]);
  const [dmsLocations, setDmsLocations] =
    React.useState<DealerLocation[]>(locations);
  const [locationToMap, setLocationToMap] = React.useState<DealerLocation>();
  const [selectedShopifyLocationId, setSelectedShopifyLocationId] =
    React.useState<number>(0);
  const [openMapper, setOpenMapper] = React.useState<boolean>(false);
  const [navigateOpen, setNavigateOpen] = React.useState(false);

  const [storeLocationMutation, storeLocationMutationResult] =
    useGetStoreLocationsMutation();

  React.useEffect(() => {
    storeLocationMutation({
      accessToken: shopifyAccessToken ?? "",
      storeUrl: shopifyUrl ?? "",
      setupType: setupType,
    }).then((res: any) => {
      if (res.data && res.data.status === 200) {
        if (setupType === SetupType.Shopify) {
          setStoreLocations(
            res?.data?.entityList?.map((loc: any) => ({
              id: loc.id,
              name: loc.name,
              address: loc.address1,
            }))
          );
        } else if (setupType === SetupType.BigCommerce) {
          setStoreLocations(
            res?.data?.entityList?.map((loc: any) => ({
              id: loc.id,
              name: loc.label,
              address: loc.address.address1,
            }))
          );
        }
      }
    });
  }, []);

  React.useEffect(() => {
    if (storeLocations && storeLocations.length > 0) {
      let mappedShopifyLocationIds = new Set();
      let preMapLocations = locations
        .filter((loc) => loc.name !== null && loc.address !== null)
        .map((location) => {
          const match = storeLocations.find(
            (shopifyLocation) =>
              shopifyLocation?.name?.trim()?.toLowerCase() ===
                location?.name?.trim()?.toLowerCase() &&
              !mappedShopifyLocationIds.has(shopifyLocation.id)
          );

          if (match) {
            mappedShopifyLocationIds.add(match.id);
            return { ...location, shopifyLocationId: match.id };
          } else {
            return { ...location, shopifyLocationId: 0 };
          }
        });
      setDmsLocations(preMapLocations);
      preMapLocations.map((loc) =>
        loc.shopifyLocationId === 0
          ? dispatch(removeLocation(loc.dealerId.toString()))
          : loc
      );
    }
  }, [storeLocations]);

  React.useEffect(() => {
    if (storeLocationMutationResult.isSuccess) {
      setStoreLocations(storeLocationMutationResult.data?.entityList);
    }
  }, [storeLocationMutationResult]);

  React.useEffect(() => {}, []);

  function isLocationExist(item: number) {
    return storeLocations.some((i: any) => i.id === item);
  }

  const handleLocationMapChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = parseInt(event.target.value, 10);
    setSelectedShopifyLocationId(value);
  };

  const handleClose = () => {
    setOpenMapper(false);
    setSelectedShopifyLocationId(0);
  };

  const handleSaveMapping = () => {
    let updatedLocations = dmsLocations.map((loc) =>
      loc.dealerId === locationToMap?.dealerId
        ? { ...loc, shopifyLocationId: selectedShopifyLocationId }
        : loc
    );
    setDmsLocations(updatedLocations);
    setSelectedShopifyLocationId(0);
    setOpenMapper(false);
  };

  const handleRemoveLocationFromMapping = (
    shopLocationId: number,
    dealerId: number
  ) => {
    let updatedLocations = dmsLocations.map((loc) =>
      loc.shopifyLocationId === shopLocationId
        ? { ...loc, shopifyLocationId: 0 }
        : loc
    );
    setDmsLocations(updatedLocations);
    dispatch(removeLocation(dealerId.toString()));
  };

  const handleNextPage = () => {
    dispatch(
      addMappedLocations(
        dmsLocations.filter((loc) => loc.shopifyLocationId > 0)
      )
    );
    handleNext();
  };
  const handleGetShopifyLocation = (shopLocationId: number) => {
    let shopLocation = storeLocations.find((loc) => loc.id === shopLocationId);
    if (shopLocation) {
      return shopLocation?.name + " (" + shopLocation?.address + ").";
    }
  };
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            p: 0.5,
            mt: 2,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setNavigateOpen(true);
            }}
          >
            Back
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleNextPage}
            disabled={locationIds.length === 0}
          >
            Next
          </Button>
        </Box>
        <FormControl
          sx={{ m: 3 }}
          component="fieldset"
          variant="standard"
          style={{ alignItems: "center" }}
        >
          <FormLabel component="h2" style={{ fontSize: "1.5rem" }}>
            Please Select The Locations You Want To Sync To Website
          </FormLabel>
          {storeLocationMutationResult.isLoading ? (
            <CircularProgress size="1.2rem" color="inherit" />
          ) : (
            <FormGroup>
              {dmsLocations.map((item, index) => {
                return (
                  <>
                    <FormControlLabel
                      key={index}
                      control={
                        <Checkbox
                          checked={locationIds.some(
                            (i: any) => i == item.dealerId.toString()
                          )}
                          onChange={(e) => {
                            console.log(e);
                            if (e.target.checked) {
                              dispatch(addLocation(item.dealerId.toString()));
                            } else {
                              dispatch(
                                removeLocation(item.dealerId.toString())
                              );
                            }
                          }}
                          name={item?.dmsLocationId.toString()}
                        />
                      }
                      label={
                        <>
                          {item?.name ?? ""}{" "}
                          <Typography component="span" fontSize="small">
                            {item?.address ? "(" + item.address + ")" : null}
                          </Typography>
                        </>
                      }
                      disabled={!isLocationExist(item.shopifyLocationId)}
                    />
                    {item.shopifyLocationId > 0 ? (
                      <Typography
                        mt={-1}
                        ml={4}
                        variant="caption"
                        color="green"
                      >
                        {" "}
                        {handleGetShopifyLocation(item.shopifyLocationId)}
                        <Link
                          sx={{ cursor: "pointer", fontStyle: "italic" }}
                          onClick={() => {
                            handleRemoveLocationFromMapping(
                              item.shopifyLocationId,
                              item.dealerId
                            );
                          }}
                        >
                          Remove
                        </Link>
                      </Typography>
                    ) : (
                      <Typography
                        mt={-1}
                        ml={4}
                        variant="caption"
                        color="error"
                      >
                        This location is not mapped to any location in Shopify.{" "}
                        <Link
                          sx={{ cursor: "pointer", fontStyle: "italic" }}
                          onClick={() => {
                            setLocationToMap(item);
                            setOpenMapper(true);
                          }}
                        >
                          Map now
                        </Link>
                      </Typography>
                    )}
                  </>
                );
              })}
            </FormGroup>
          )}
        </FormControl>
      </Box>

      <Dialog
        fullWidth
        open={openMapper}
        onClose={handleClose}
        aria-labelledby="draggable-dialog-title"
      >
        <DialogTitle
          style={{ textAlign: "center" }}
          id="draggable-dialog-title"
        >
          Location Mapper
        </DialogTitle>
        <DialogContent sx={{ width: "auto" }}>
          <Paper
            elevation={3}
            sx={{ marginTop: 1, padding: "15px", borderRadius: "5px" }}
          >
            <Typography fontSize="small">
              Select any location from the followings to map with selected.
            </Typography>
            <FormControl sx={{ mt: 1 }}>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={selectedShopifyLocationId}
                onChange={handleLocationMapChange}
                sx={{ display: "flex", flexDirection: "column" }}
              >
                <Grid container spacing={2}>
                  {storeLocations.map((loc) => (
                    <Grid item xs={6}>
                      <FormControlLabel
                        value={loc.id}
                        disabled={dmsLocations.some(
                          (location) => location.shopifyLocationId === loc.id
                        )}
                        control={<Radio />}
                        label={
                          <>
                            <Typography component="span" fontSize="0.8rem">
                              {loc?.name ?? ""}
                            </Typography>
                            <Typography component="span" fontSize="0.6rem">
                              {loc.address ? " (" + loc?.address + ")" : ""}
                            </Typography>
                          </>
                        }
                        sx={{ fontSize: "0.9rem" }}
                      />
                    </Grid>
                  ))}
                </Grid>
              </RadioGroup>
            </FormControl>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            variant="outlined"
            autoFocus
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              handleSaveMapping();
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <AppConfirmDialog
        headerText="Confirm Navigation"
        bodyText="Are you sure you want to go back? You will lose Locations mapping."
        onFail={() => {
          setNavigateOpen(false);
        }}
        onSuccess={() => {
          setNavigateOpen(false);
          handleBack();
        }}
        handleClose={() => {
          setNavigateOpen(false);
        }}
        isOpen={navigateOpen}
      />
    </>
  );
};

export default DmsLocations;
